import React from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { BookOpen, Calendar, Clock, Eye, FileText, FolderOpen, Image as ImageIcon, Minus, Paperclip, ExternalLink as PopoutIcon, Send, Smile, Table, Trash2, X } from 'lucide-react';
import { AttachmentSizeBar } from './AttachmentSizeBar';
import { AiEmailButton } from './AiEmailButton';

const MailComposeModal = ({
  EMOJI_LIST,
  attachments,
  closeComposeModal,
  composeForm,
  composeModalDragging,
  composeModalPosition,
  composeModalRef,
  emailAccounts,
  handleDragEnter,
  handleDragLeave,
  handleDragOver,
  handleDrop,
  handleFileSelect,
  handleImageInsert,
  imageInputRef,
  insertEmoji,
  insertTable,
  isDragging,
  minimizeCurrentWindow,
  openComposeInNewWindow,
  openContactPicker,
  quillFormats,
  quillModules,
  quillRef,
  removeAttachment,
  replyMode,
  selectedAccount,
  sendEmail,
  setComposeForm,
  setComposeModalDragOffset,
  setComposeModalDragging,
  setPreviewModal,
  setSelectedAccount,
  setShowCRMAttachmentPicker,
  setShowEmojiPicker,
  setShowTableDialog,
  setTableCols,
  setTableRows,
  showEmojiPicker,
  showTableDialog,
  tableCols,
  tableRows
}) => {
  const { t } = useTranslation('admin');
  return (
    <>
        <div data-testid="mail-compose-modal" className="fixed z-[1200] inset-0 bg-black/30" onClick={minimizeCurrentWindow}>
          <div 
            ref={composeModalRef}
            onClick={(e) => e.stopPropagation()}
            className={`bg-white shadow-2xl rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto ${
              composeModalPosition.x === null ? 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2' : ''
            }`}
            style={composeModalPosition.x !== null ? {
              position: 'fixed',
              left: composeModalPosition.x,
              top: composeModalPosition.y,
              transform: 'none'
            } : {}}
          >
            <div 
              className={`p-4 border-b border-slate-200 flex items-center justify-between ${
                composeModalDragging ? 'cursor-grabbing' : 'cursor-grab'
              }`}
              onMouseDown={(e) => {
                if (composeModalRef.current) {
                  const rect = composeModalRef.current.getBoundingClientRect();
                  setComposeModalDragOffset({
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top
                  });
                  setComposeModalDragging(true);
                }
              }}
            >
              <h3 className="font-bold text-slate-900 truncate text-xl">
                {replyMode === 'reply' ? t('mail.reply') : replyMode === 'forward' ? t('mail.forward') : t('mail.newMessage')}
              </h3>
              <div className="flex items-center space-x-1">
                {/* Bouton ouvrir dans nouvelle fenêtre */}
                <button 
                  data-testid="mail-compose-popout"
                  onClick={(e) => {
                    e.stopPropagation();
                    openComposeInNewWindow();
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                  title={t('mail.openNewWindow')}
                >
                  <PopoutIcon size={18} />
                </button>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    minimizeCurrentWindow();
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                  title={t('mail.minimize')}
                >
                  <Minus size={18} />
                </button>
                <button 
                  data-testid="mail-compose-close"
                  onClick={closeComposeModal} 
                  onMouseDown={(e) => e.stopPropagation()}
                  className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                  title={t('mail.close')}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <>
              <div className="p-6 space-y-4">
              {/* Sélecteur du compte expéditeur */}
              {emailAccounts.length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('mail.fieldFromRequired')}</label>
                  <select
                    value={selectedAccount?.id || ''}
                    onChange={(e) => {
                      const account = emailAccounts.find(a => a.id === e.target.value);
                      setSelectedAccount(account);
                    }}
                    className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 bg-white"
                  >
                    {emailAccounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.display_name || account.email_address} ({account.email_address})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Affichage du compte si un seul */}
              {emailAccounts.length === 1 && selectedAccount && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('mail.fieldFrom')}</label>
                  <div className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-600">
                    {selectedAccount.display_name || selectedAccount.email_address}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('mail.fieldToRequired')}</label>
                <div className="relative">
                  <input
                    type="text"
                    value={composeForm.to}
                    onChange={(e) => setComposeForm({...composeForm, to: e.target.value})}
                    placeholder={t('mail.toPlaceholder')}
                    className="w-full px-4 py-2 pr-12 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={() => openContactPicker('to')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                    title={t('mail.pickFromDirectory')}
                  >
                    <BookOpen size={18} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('mail.fieldCc')}</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={composeForm.cc}
                      onChange={(e) => setComposeForm({...composeForm, cc: e.target.value})}
                      placeholder={t('mail.ccPlaceholder')}
                      className="w-full px-4 py-2 pr-12 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => openContactPicker('cc')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      title={t('mail.pickFromDirectory')}
                    >
                      <BookOpen size={18} />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('mail.fieldBcc')}</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={composeForm.bcc}
                      onChange={(e) => setComposeForm({...composeForm, bcc: e.target.value})}
                      placeholder={t('mail.bccPlaceholder')}
                      className="w-full px-4 py-2 pr-12 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => openContactPicker('bcc')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      title={t('mail.pickFromDirectory')}
                    >
                      <BookOpen size={18} />
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('mail.fieldSubjectRequired')}</label>
                <input
                  type="text"
                  value={composeForm.subject}
                  onChange={(e) => setComposeForm({...composeForm, subject: e.target.value})}
                  placeholder={t('mail.subjectPlaceholder')}
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('mail.fieldMessage')}</label>
                
                {/* Boutons supplémentaires (Emoji, Tableau, Image) */}
                <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-200">
                  {/* Bouton Emoji */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                      title={t('mail.insertEmoji')}
                    >
                      <Smile size={16} />
                      <span>{t('mail.emoji')}</span>
                    </button>
                    
                    {showEmojiPicker && (
                      <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-xl p-3 w-72">
                        <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-100">
                          <span className="text-sm font-medium text-slate-700">{t('mail.chooseEmoji')}</span>
                          <button
                            onClick={() => setShowEmojiPicker(false)}
                            className="text-slate-400 hover:text-slate-600"
                          >
                            <X size={16} />
                          </button>
                        </div>
                        <div className="grid grid-cols-8 gap-1 max-h-48 overflow-y-auto">
                          {EMOJI_LIST.map((emoji, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => insertEmoji(emoji)}
                              className="text-xl p-1.5 hover:bg-slate-100 rounded transition-colors"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Bouton Tableau */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowTableDialog(!showTableDialog)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                      title={t('mail.insertTable')}
                    >
                      <Table size={16} />
                      <span>{t('mail.table')}</span>
                    </button>
                    
                    {showTableDialog && (
                      <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-xl p-4 w-56">
                        <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-100">
                          <span className="text-sm font-medium text-slate-700">{t('mail.insertTable')}</span>
                          <button
                            onClick={() => setShowTableDialog(false)}
                            className="text-slate-400 hover:text-slate-600"
                          >
                            <X size={16} />
                          </button>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs text-slate-600 mb-1">{t('mail.rows')}</label>
                            <input
                              type="number"
                              min="1"
                              max="20"
                              value={tableRows}
                              onChange={(e) => setTableRows(parseInt(e.target.value) || 1)}
                              className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-slate-600 mb-1">{t('mail.columns')}</label>
                            <input
                              type="number"
                              min="1"
                              max="10"
                              value={tableCols}
                              onChange={(e) => setTableCols(parseInt(e.target.value) || 1)}
                              className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={insertTable}
                            className="w-full px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
                          >
                            {t('mail.insert')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Bouton Image */}
                  <button
                    type="button"
                    onClick={() => imageInputRef.current?.click()}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                    title={t('mail.insertImage')}
                  >
                    <ImageIcon size={16} />
                    <span>{t('mail.image')}</span>
                  </button>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageInsert}
                    className="hidden"
                  />
                </div>
                
                {/* Éditeur Quill */}
                <div className="flex justify-end mb-2">
                  <AiEmailButton
                    subject={composeForm.subject}
                    currentBody={composeForm.body}
                    onInsert={(html) => setComposeForm({ ...composeForm, body: (html || '') + (composeForm.body || '') })}
                  />
                </div>
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={composeForm.body}
                    onChange={(content) => setComposeForm({...composeForm, body: content})}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder={t('mail.messagePlaceholder')}
                    className="mailhub-editor"
                  />
                </div>
                
                {/* Styles pour Quill */}
                <style>{`
                  .mailhub-editor .ql-container {
                    min-height: 250px;
                    max-height: 400px;
                    overflow-y: auto;
                    font-size: 14px;
                  }
                  .mailhub-editor .ql-editor {
                    min-height: 250px;
                  }
                  .mailhub-editor .ql-toolbar {
                    border: none;
                    border-bottom: 1px solid #e2e8f0;
                    background-color: #f8fafc;
                  }
                  .mailhub-editor .ql-container {
                    border: none;
                  }
                  .mailhub-editor .ql-editor.ql-blank::before {
                    color: #94a3b8;
                    font-style: normal;
                  }
                `}</style>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('mail.attachmentsLabel')}</label>
                <div className="space-y-3">
                  {/* Zone upload + Bouton CRM */}
                  <div className="flex gap-3">
                    <div
                      onDragEnter={handleDragEnter}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`flex-1 flex items-center justify-center px-4 py-6 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                        isDragging 
                          ? 'border-indigo-600 bg-indigo-100' 
                          : 'border-slate-300 hover:border-indigo-500 hover:bg-indigo-50'
                      }`}
                    >
                      <label className="flex items-center justify-center cursor-pointer w-full">
                        <Paperclip size={20} className={`mr-2 ${isDragging ? 'text-indigo-600' : 'text-slate-400'}`} />
                        <span className={isDragging ? 'text-indigo-700 font-medium' : 'text-slate-600'}>
                          {isDragging ? t('mail.dropFilesHere') : t('mail.clickOrDrag')}
                        </span>
                        <input
                          type="file"
                          multiple
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                      </label>
                    </div>
                    
                    {/* Bouton Parcourir CRM */}
                    <button
                      type="button"
                      data-testid="mail-browse-crm-btn"
                      onClick={() => setShowCRMAttachmentPicker(true)}
                      className="flex flex-col items-center justify-center px-6 py-4 border-2 border-dashed border-red-300 rounded-xl hover:border-red-500 hover:bg-red-50 transition-all"
                    >
                      <FolderOpen size={24} className="text-red-500 mb-1" />
                      <span className="text-sm font-medium text-red-600">{t('mail.browseCrm')}</span>
                    </button>
                  </div>

                  {attachments.length > 0 && (
                    <div className="space-y-2">
                      {attachments.map((attachment, index) => (
                        <div key={index} className={`flex items-center justify-between p-3 rounded-xl border ${
                          attachment.fromCRM 
                            ? 'bg-red-50 border-red-200' 
                            : 'bg-slate-50 border-slate-200'
                        }`}>
                          <div className="flex items-center flex-1 min-w-0">
                            <FileText size={20} className={attachment.fromCRM ? 'text-red-400 mr-3' : 'text-slate-400 mr-3'} />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-slate-900 truncate">{attachment.name}</p>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-slate-500">{(attachment.size / 1024).toFixed(2)} KB</span>
                                {attachment.fromCRM && (
                                  <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                                    {attachment.source === 'clients' ? t('mail.linkedClient') : 
                                     attachment.source === 'courtiers' ? t('mail.linkedCourtier') :
                                     attachment.source === 'companies' ? t('mail.linkedCompany') : t('mail.srcCabinet')}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 ml-2">
                            <button
                              onClick={() => {
                                setPreviewModal({ show: true, url: attachment.preview, filename: attachment.name, mimeType: attachment.file?.type || 'application/octet-stream' });
                              }}
                              className="text-indigo-600 hover:text-indigo-800 p-2 hover:bg-indigo-50 rounded-lg transition-colors"
                              title={t('mail.preview')}
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              onClick={() => removeAttachment(index)}
                              className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors"
                              title={t('mail.deleteShort')}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <AttachmentSizeBar attachments={attachments} />
                </div>
              </div>

              {/* Option AR (Accusé de Réception) */}
              <div className="pt-4 border-t border-slate-200">
                <label className="flex items-center space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={composeForm.enableTracking}
                    onChange={(e) => setComposeForm({...composeForm, enableTracking: e.target.checked})}
                    className="w-5 h-5 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                  />
                  <div className="flex items-center space-x-2">
                    <Eye size={18} className={composeForm.enableTracking ? 'text-indigo-600' : 'text-slate-400'} />
                    <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">
                      {t('mail.enableTracking')}
                    </span>
                  </div>
                </label>
                <p className="text-xs text-slate-500 mt-2 ml-8">
                  {t('mail.trackingHint')}
                </p>
              </div>

              {/* Option Envoi Programmé */}
              <div className="pt-4 border-t border-slate-200">
                <label className="flex items-center space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={composeForm.scheduledSend}
                    onChange={(e) => setComposeForm({...composeForm, scheduledSend: e.target.checked})}
                    className="w-5 h-5 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                  />
                  <div className="flex items-center space-x-2">
                    <Clock size={18} className={composeForm.scheduledSend ? 'text-indigo-600' : 'text-slate-400'} />
                    <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">
                      {t('mail.scheduleSend')}
                    </span>
                  </div>
                </label>
                
                {composeForm.scheduledSend && (
                  <div className="mt-3 ml-8 grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">
                        <Calendar size={12} className="inline mr-1" />
                        {t('mail.dateField')}
                      </label>
                      <input
                        type="date"
                        value={composeForm.scheduledDate}
                        onChange={(e) => setComposeForm({...composeForm, scheduledDate: e.target.value})}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">
                        <Clock size={12} className="inline mr-1" />
                        {t('mail.timeField')}
                      </label>
                      <input
                        type="time"
                        value={composeForm.scheduledTime}
                        onChange={(e) => setComposeForm({...composeForm, scheduledTime: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-slate-500 mt-2 ml-8">
                  {t('mail.scheduleHint')}
                </p>
              </div>
                </div>

                <div className="p-6 border-t border-slate-200 flex items-center justify-end space-x-3">
                  <button
                    onClick={closeComposeModal}
                    className="px-6 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors"
                  >
                    {t('mail.cancelBtn')}
                  </button>
                  <button
                    data-testid="mail-compose-send"
                    onClick={sendEmail}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors flex items-center space-x-2"
                  >
                    <Send size={16} />
                    <span>{t('mail.send')}</span>
                  </button>
                </div>
              </>
          </div>
        </div>
    </>
  );
};

export default MailComposeModal;
